import {
  HomepageBannerFragment,
  useBrowser,
  useGetHomepageBannerByLocalizationIdQuery,
} from "@stepapp/core";
import {
  Block,
  Carousel,
  ImageBackground,
  LinePlaceholder,
  palette,
  spacing,
  Touchable,
  Typography,
  useBreakpoints,
} from "@stepapp/ui";
import React, { memo } from "react";
import { StyleSheet, useWindowDimensions } from "react-native";
import { useAppSelector } from "../../src/hooks";
import { AppStorage } from "../../src/services/AppStorage";

const useImageDimensions = () => {
  const { sm } = useBreakpoints();
  const { width: screenWidth } = useWindowDimensions();

  const screenContainerWidth = screenWidth > 1160 ? 1160 : screenWidth;
  const imgWidth = screenContainerWidth - (sm ? 24 : 40);
  const imgHeight = sm ? 45 : 26.25;

  return { imgWidth, imgHeight };
};

const BannerContent = memo(({ data }: { data?: HomepageBannerFragment }) => {
  const {
    title,
    description,
    buttonText,
    url,
    isButtonVisible,
    isDescriptionVisible,
    isTitleVisible,
    isUrlVisible,
  } = data || {};

  const handlePress = useBrowser(url || "");

  return (
    <>
      {isTitleVisible && (
        <Typography style={styles.title} variant="sectionTitle">
          {title}
        </Typography>
      )}

      {isDescriptionVisible && <Typography style={styles.description}>{description}</Typography>}

      {isButtonVisible && (
        <Block flex={1} marginBottom={1.25} style={styles.footer}>
          <Touchable onPress={isUrlVisible ? handlePress : undefined} style={styles.button}>
            <Typography type="white" variant="bodyBold">
              {buttonText}
            </Typography>
          </Touchable>
        </Block>
      )}
    </>
  );
});

const HomePageBanner = () => {
  const { mobileOnly } = useBreakpoints();
  const country = AppStorage().get("countryCode");
  const localizationId = useAppSelector(({ appConfig }) => appConfig.localizationId);
  const { data, loading } = useGetHomepageBannerByLocalizationIdQuery({
    variables: { data: { localizationId } },
    fetchPolicy: "cache-and-network",
  });

  const { imgWidth, imgHeight } = useImageDimensions();
  const { desktopImgUrl, mobileImgUrl, carousel } = data?.getHomepageBannerByLocalizationId || {};

  const singleImage = mobileOnly ? mobileImgUrl : desktopImgUrl;
  const showSingleImage = !carousel?.length && !loading && singleImage;
  const showPlaceholder = loading && !data?.getHomepageBannerByLocalizationId && country === "PL";
  const showCarousel = !!carousel?.length;

  if (showPlaceholder) {
    return (
      <LinePlaceholder
        marginTop={2.5}
        marginBottom={3.5}
        borderRadius={1.25}
        height={imgHeight}
        width={imgWidth / 8}
      />
    );
  }

  return (
    <>
      {showSingleImage && (
        <ImageBackground
          contentContainerStyle={styles.contentContainer}
          contentFit="cover"
          imageStyle={styles.image}
          source={singleImage || ""}
          style={[
            styles.container,
            { marginBottom: spacing.unit * (mobileOnly ? 3 : 4) },
            { height: spacing.unit * imgHeight },
          ]}
        >
          <BannerContent data={data?.getHomepageBannerByLocalizationId} />
        </ImageBackground>
      )}

      {showCarousel && (
        <Block style={styles.carouselContainer}>
          <Carousel
            data={carousel}
            width={imgWidth}
            height={spacing.unit * imgHeight}
            style={styles.carousel}
          />
        </Block>
      )}
    </>
  );
};

export default React.memo(HomePageBanner);

const styles = StyleSheet.create({
  button: {
    backgroundColor: palette.primary.accent,
    borderRadius: spacing.radius,
    paddingHorizontal: spacing.unit * 2,
    padding: spacing.unit,
  },
  container: {
    marginTop: spacing.unit * 1.75,
  },
  contentContainer: {
    paddingHorizontal: spacing.unit * 3.25,
    paddingVertical: spacing.unit * 2,
  },
  description: {
    color: palette.primary.accent,
  },
  image: {
    borderRadius: spacing.radius,
  },
  footer: {
    alignItems: "flex-start",
    justifyContent: "flex-end",
  },
  title: {
    color: palette.primary.accent,
    lineHeight: 35,
  },
  carouselContainer: {
    marginBottom: spacing.unit * 4,
    marginTop: spacing.unit * 2,
  },
  carousel: {
    borderRadius: spacing.radius,
  },
});
